import { createReadStream } from 'node:fs';
import { createInterface } from 'node:readline';
import type { LogEntry, TraceBuilder, RecentTraceCallsResponse } from '../types';
import { fileExists, parseLogLine, extractNumber } from '../utils';
import { readFileReverse } from './file-reader';

/**
 * Read all automation trigger list in trace.log
 * filter by request_body.trigger & limit & path=/__innerapi__/automation/invoke
 */
export async function readTriggerList(
  filePath: string,
  trigger: string,
  path: string,
  limit?: number,
  triggerID?: string,
): Promise<RecentTraceCallsResponse | undefined> {
  if (!(await fileExists(filePath))) {
    return undefined;
  }

  const config = {
    maxEntriesPerTrace: 10,
    chunkSize: 64 * 1024,
  };

  const builders = new Map<string, TraceBuilder>();
  const completedCalls: TraceBuilder[] = [];

  const createTraceBuilder = (traceId: string): TraceBuilder => ({
    traceId,
    entries: [],
    method: undefined,
    path: undefined,
    startTime: undefined,
    endTime: undefined,
    statusCode: undefined,
    durationMs: undefined,
    hasCompleted: false,
  });

  const shouldIncludeInCompletedCalls = (builder: TraceBuilder): boolean => {
    const alreadyAdded = completedCalls.some(call => call.traceId === builder.traceId);
    if (alreadyAdded) {
      return false;
    }

    const isAutomationTrigger = builder.path?.endsWith(path);
    if (!isAutomationTrigger) {
      return false;
    }

    if (trigger && builder.entries.length > 0) {
      const requestEntry = builder.entries.find(e => e.request_body?.trigger);
      if (requestEntry?.request_body?.trigger) {
        return String(requestEntry.request_body.trigger) === trigger
          && (triggerID ? requestEntry?.request_body?.triggerID === triggerID : true);
      }
      return false;
    }

    return true;
  };

  const updateBuilderMetadata = (builder: TraceBuilder, entry: LogEntry): void => {
    if (entry.method && !builder.method) builder.method = String(entry.method);
    if (entry.path && !builder.path) builder.path = String(entry.path);

    builder.entries.push(entry);
    if (builder.entries.length > config.maxEntriesPerTrace) {
      builder.entries.shift();
    }

    if (shouldIncludeInCompletedCalls(builder)) {
      completedCalls.push(builder);
      if (limit && completedCalls.length > limit) {
        completedCalls.pop();
      }
    }
  };

  const handleRequestCompleted = (builder: TraceBuilder, entry: LogEntry, message: string): void => {
    builder.hasCompleted = true;
    builder.endTime = entry.time;
    builder.statusCode = extractNumber(message, /status_code:\s*(\d+)/);
    builder.durationMs = extractNumber(message, /duration_ms:\s*(\d+)/);
    if (!builder.path && entry.path) {
      builder.path = String(entry.path);
    }

    if (shouldIncludeInCompletedCalls(builder)) {
      completedCalls.push(builder);
      if (limit && completedCalls.length > limit) {
        completedCalls.pop();
      }
    }
  };

  const processLogEntry = (entry: LogEntry): void => {
    const { trace_id: traceId, message = '' } = entry;
    if (!traceId) return;

    let builder = builders.get(traceId);
    if (!builder) {
      builder = createTraceBuilder(traceId);
      builders.set(traceId, builder);
    }

    updateBuilderMetadata(builder, entry);

    if (!builder.hasCompleted && (message.includes('HTTP request completed') || message.includes('HTTP request failed'))) {
      handleRequestCompleted(builder, entry, message);
    }

    if (message.includes('HTTP request started') && !builder.startTime) {
      builder.startTime = entry.time;
    }
  };

  const processLine = (line: string): void => {
    const entry = parseLogLine(line);
    if (entry?.trace_id) {
      processLogEntry(entry);
    }
  };

  await readFileReverse(filePath, config.chunkSize, processLine);

  return {
    page: 1,
    pageSize: completedCalls.length,
    totalCalls: completedCalls.length,
    totalPages: 1,
    calls: completedCalls.map((builder) => ({
      traceId: builder.traceId,
      method: builder.method,
      path: builder.path,
      startTime: builder.startTime,
      endTime: builder.endTime,
      statusCode: builder.statusCode,
      durationMs: builder.durationMs,
      entries: builder.entries.slice().reverse(),
    })),
  };
}

/**
 * Read trigger detail entries by instance ID
 */
export async function readTriggerDetail(
  filePath: string,
  path: string,
  instanceID: string,
): Promise<{ instanceID: string; entries: LogEntry[] } | undefined> {
  const exists = await fileExists(filePath);
  if (!exists) {
    return undefined;
  }

  const matches: LogEntry[] = [];
  const stream = createReadStream(filePath, { encoding: 'utf8' });
  const rl = createInterface({ input: stream, crlfDelay: Infinity });

  for await (const line of rl) {
    const entry = parseLogLine(line);
    if (!entry) continue;

    const isAutomationTrigger = entry.path?.endsWith(path);
    const hasInstanceID = entry.instance_id === instanceID && entry.trigger;
    if (!isAutomationTrigger || !hasInstanceID) continue;

    matches.push(entry);
  }

  rl.close();
  stream.close();

  return {
    instanceID,
    entries: matches,
  };
}
